import {
  useCallback,
  useEffect,
  useMemo,
  useOptimistic,
  useRef,
  useTransition,
} from "react";
import { useRouter, useSearchParams } from "next/navigation";
import type { SortingState } from "@/components/common/data-table/DataTable";
import { getListFilters, saveListFilters } from "./usePersistedFilters";
import type { FilterConfig } from "@/lib/types/data.types";

// ─── Hook config & return ────────────────────────────────────────────────────

export interface UseTableSearchParamsConfig {
  /** URL prefix for navigation (e.g. "/property", "/multi-rater"). */
  basePath: string;
  /** Column to sort by when no sort params are in the URL. */
  defaultSortColumn?: string;
  /** Sort direction for the default sort column. Defaults to "desc". */
  defaultSortDirection?: "asc" | "desc";
  /** When true, the hook manages `show_archived` URL state with optimistic updates. */
  archivable?: boolean;
}

export interface UseTableSearchParamsReturn<
  TFilter extends FilterConfig = FilterConfig,
> {
  searchParams: URLSearchParams;
  isPending: boolean;
  startTransition: React.TransitionStartFunction;
  /**
   * Sets each key to its value; a `null` value deletes the key. Updates are
   * applied in one navigation so column/search/page changes stay atomic.
   */
  updateSearchParams: (
    updates: Record<string, string | number | null>,
  ) => void;
  handlePageChange: (page: number) => void;
  handlePageSizeChange: (pageSize: number) => void;
  handleSearchChange: (value: string) => void;
  handleSortChange: (sorting: SortingState) => void;
  currentFilters: TFilter[];
  handleFiltersChange: (filters: TFilter[]) => void;
  currentSearch: string;
  /** Currently selected search column key (`search_column`), or "" when none. */
  currentSearchColumn: string;
  /**
   * Selects a search column: sets `search_column`, clears `search`, and resets
   * to page 1 — atomically, so a stale value is never reinterpreted under a new
   * column's kind.
   */
  handleSearchColumnChange: (key: string) => void;
  initialSorting: SortingState;
  /** Whether the archive view is currently active (optimistic). Only meaningful when `archivable` is true. */
  showArchived: boolean;
  /** Toggle between active and archived views. Resets page to 1. No-op when `archivable` is false. */
  handleToggleArchived: (next: boolean) => void;
}

export function parseTableFilters<TFilter extends FilterConfig = FilterConfig>(
  rawFilters: string | null,
): TFilter[] {
  try {
    return rawFilters ? JSON.parse(rawFilters) : [];
  } catch {
    return [];
  }
}

// ─── Hook ────────────────────────────────────────────────────────────────────

/**
 * Generic hook for URL-based table state management.
 *
 * Encapsulates pagination, search, sorting, and filter state as URL search
 * params so the server page can read them on each navigation. Persists the
 * current param string to sessionStorage via `saveListFilters`.
 *
 * All returned `searchParams` are optimistic — they reflect the intended
 * URL state immediately during transitions, before the server re-renders.
 */
export function useTableSearchParams<
  TFilter extends FilterConfig = FilterConfig,
>(
  config: UseTableSearchParamsConfig,
): UseTableSearchParamsReturn<TFilter> {
  const {
    basePath,
    defaultSortColumn,
    defaultSortDirection = "desc",
    archivable = false,
  } = config;

  const router = useRouter();
  const realSearchParams = useSearchParams();
  const [isPending, startTransition] = useTransition();

  // Optimistic params: reflects intended URL state during transitions
  const [optimisticParamsString, setOptimisticParams] = useOptimistic(
    realSearchParams.toString(),
  );

  const searchParams = useMemo(function parseOptimisticParams() {
    return new URLSearchParams(optimisticParamsString);
  }, [optimisticParamsString]);

  // ── Archive toggle (optimistic) ─────────────────────────────
  const showArchived = archivable
    ? searchParams.get("show_archived") === "true"
    : false;

  // Persist to sessionStorage so the sidebar can restore them
  useEffect(function persistFilters() {
    const params = realSearchParams.toString();
    if (params) saveListFilters(basePath, params);
  }, [realSearchParams, basePath]);

  // Auto-restore previously-saved params on first mount when the URL is empty.
  // Caveat: routes that SSR-redirect with an injected param (e.g. /multi-rater
  // and /market-finder inject `office_ids` for non-admins) will never see an empty
  // URL here, so the sidebar is the restoration path for those flows.
  const didRestoreRef = useRef(false);
  useEffect(function autoRestoreFilters() {
    if (didRestoreRef.current) return;
    didRestoreRef.current = true;
    if (realSearchParams.toString() !== "") return;
    const saved = getListFilters(basePath);
    if (!saved) return;
    startTransition(function restore() {
      setOptimisticParams(saved);
      router.replace(`${basePath}?${saved}`, { scroll: false });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-once
  }, []);

  // ── Navigation helper ───────────────────────────────────────

  const pushWithOptimisticParams = useCallback(
    function pushOptimistic(nextParams: URLSearchParams) {
      const nextParamsString = nextParams.toString();
      startTransition(function navigateOptimistically() {
        setOptimisticParams(nextParamsString);
        router.push(`${basePath}?${nextParamsString}`);
      });
    },
    [startTransition, setOptimisticParams, basePath, router],
  );

  // ── Core param updater ──────────────────────────────────────

  const updateSearchParams = useCallback(
    function updateParams(updates: Record<string, string | number | null>) {
      const nextParams = new URLSearchParams(optimisticParamsString);
      Object.entries(updates).forEach(function applyUpdate([key, value]) {
        if (value === null) {
          nextParams.delete(key);
        } else {
          nextParams.set(key, String(value));
        }
      });
      pushWithOptimisticParams(nextParams);
    },
    [optimisticParamsString, pushWithOptimisticParams],
  );

  // ── Pagination ──────────────────────────────────────────────

  const handlePageChange = useCallback(
    function changePage(newPage: number) {
      updateSearchParams({ page: newPage });
    },
    [updateSearchParams],
  );

  const handlePageSizeChange = useCallback(
    function changePageSize(newPageSize: number) {
      updateSearchParams({ page: 1, per_page: newPageSize });
    },
    [updateSearchParams],
  );

  // ── Search ──────────────────────────────────────────────────

  const handleSearchChange = useCallback(
    function changeSearch(value: string) {
      // Drop the param entirely when empty so a cleared box leaves no `search=`
      // in the URL; keep the selected column intact.
      updateSearchParams({ search: value === "" ? null : value, page: 1 });
    },
    [updateSearchParams],
  );

  const currentSearch = searchParams.get("search") || "";

  // ── Search column (BAR-227) ─────────────────────────────────

  const currentSearchColumn = searchParams.get("search_column") || "";

  const handleSearchColumnChange = useCallback(
    function changeSearchColumn(key: string) {
      updateSearchParams({ search_column: key, search: null, page: 1 });
    },
    [updateSearchParams],
  );

  // ── Sorting ─────────────────────────────────────────────────

  const handleSortChange = useCallback(
    function changeSort(sorting: SortingState) {
      if (sorting.length > 0) {
        updateSearchParams({
          sort_column: sorting[0].id,
          sort_direction: sorting[0].desc ? "desc" : "asc",
          page: 1,
        });
      } else {
        const nextParams = new URLSearchParams(optimisticParamsString);
        nextParams.delete("sort_column");
        nextParams.delete("sort_direction");
        nextParams.set("page", "1");
        pushWithOptimisticParams(nextParams);
      }
    },
    [updateSearchParams, optimisticParamsString, pushWithOptimisticParams],
  );

  const currentSortColumn = searchParams.get("sort_column") || "";
  const currentSortDirection = searchParams.get("sort_direction") || "";

  const initialSorting: SortingState = currentSortColumn
    ? [{ id: currentSortColumn, desc: currentSortDirection === "desc" }]
    : defaultSortColumn
      ? [{ id: defaultSortColumn, desc: defaultSortDirection === "desc" }]
      : [];

  // ── Archive toggle handler ───────────────────────────────────

  const handleToggleArchived = useCallback(
    function toggleArchived(next: boolean) {
      if (!archivable) return;
      updateSearchParams({
        show_archived: next ? "true" : "false",
        page: 1,
      });
    },
    [archivable, updateSearchParams],
  );

  // ── Filters ─────────────────────────────────────────────────

  const rawFilters = searchParams.get("filters");
  const currentFilters = useMemo<TFilter[]>(
    () => parseTableFilters<TFilter>(rawFilters),
    [rawFilters],
  );

  const handleFiltersChange = useCallback(
    function changeFilters(newFilters: TFilter[]) {
      const nextParams = new URLSearchParams(optimisticParamsString);
      if (newFilters.length > 0) {
        nextParams.set("filters", JSON.stringify(newFilters));
      } else {
        nextParams.delete("filters");
      }
      nextParams.set("page", "1");
      pushWithOptimisticParams(nextParams);
    },
    [optimisticParamsString, pushWithOptimisticParams],
  );

  return {
    searchParams,
    isPending,
    startTransition,
    updateSearchParams,
    handlePageChange,
    handlePageSizeChange,
    handleSearchChange,
    handleSortChange,
    currentFilters,
    handleFiltersChange,
    currentSearch,
    currentSearchColumn,
    handleSearchColumnChange,
    initialSorting,
    showArchived,
    handleToggleArchived,
  };
}
